import { martingaleStakeProgression, theoreticalFullStopExposure } from '../analytics/martingale-grid.ts';
import { fixedStakeProgression, theoreticalFixedFullStopExposure } from '../analytics/fixed-grid.ts';
import type { PaperStakingMode, PaperStrategyConfig } from './types.ts';

/** Live paper simulation: 2x is MARTINGALE only; every target > 2 is FIXED only. */
export function liveStakingModeForTarget(targetMultiplier: number): PaperStakingMode {
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
    throw new Error('Target multiplier must be a finite number ≥ 1.');
  }
  if (targetMultiplier === 2) {
    return 'MARTINGALE';
  }
  if (targetMultiplier > 2) {
    return 'FIXED';
  }
  throw new Error('Live paper simulation requires target 2x (Martingale) or target > 2x (Fixed).');
}

export function assertLiveStrategyConfig(config: PaperStrategyConfig): void {
  if (!config.id.trim()) {
    throw new Error('Strategy id is required.');
  }
  const required = liveStakingModeForTarget(config.targetMultiplier);
  if (config.stakingMode !== required) {
    throw new Error(
      `Live paper simulation: target ${config.targetMultiplier}x must use ${required}, not ${config.stakingMode}.`,
    );
  }
  if (!Number.isInteger(config.entryDrought) || config.entryDrought < 1) {
    throw new Error(`Strategy ${config.id}: entry D must be a positive integer.`);
  }
  if (!Number.isInteger(config.stopDrought) || config.stopDrought < config.entryDrought + 1) {
    throw new Error(`Strategy ${config.id}: stop S must be an integer ≥ D + 1.`);
  }
  if (!Number.isFinite(config.baseStake) || config.baseStake <= 0) {
    throw new Error(`Strategy ${config.id}: stake must be a finite number > 0.`);
  }
}

export function possibleWagerCount(config: PaperStrategyConfig): number {
  return config.stopDrought - config.entryDrought;
}

export function theoreticalMaxEpisodeExposure(config: PaperStrategyConfig): number {
  const wagers = possibleWagerCount(config);
  if (config.stakingMode === 'MARTINGALE') {
    return theoreticalFullStopExposure(wagers, config.baseStake);
  }
  return theoreticalFixedFullStopExposure(wagers, config.baseStake);
}

export function stakeProgression(config: PaperStrategyConfig): number[] {
  const wagers = possibleWagerCount(config);
  if (config.stakingMode === 'MARTINGALE') {
    return martingaleStakeProgression(wagers, config.baseStake);
  }
  return fixedStakeProgression(wagers, config.baseStake);
}

export function paperStrategyLabel(config: PaperStrategyConfig): string {
  return `${config.targetMultiplier}x-d${config.entryDrought}-s${config.stopDrought}`;
}
